import { TuiButton } from '@taiga-ui/core';
import { pure } from '@core/helpers/pure';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestAddressesService } from '@core/services/request/request-addresses.service';
import { RequestService } from '@core/services/request/request.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormPickerComponent } from '@core/components';
import { AttachActualDeliveryAddressesDirective } from './directives/attach-actual-delivery-addresses.directive';
import { asyncScheduler, distinctUntilChanged, Subject, switchMap } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';

enum FormFields {
  ActualDeliveryAddress = 'actualDeliveryAddress',
}

@Component({
  selector: 'x-actual-delivery-address-group-actions-view',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TuiButton,
    FormPickerComponent,
    AttachActualDeliveryAddressesDirective,
    TranslatePipe,
  ],
  templateUrl: './actual-delivery-address-group-actions-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActualDeliveryAddressGroupActionsViewComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly formBuilder = inject(FormBuilder);
  readonly FormFields = FormFields;
  public readonly requestService = inject(RequestService);
  private readonly requestAddressesService = inject(RequestAddressesService);
  readonly permissionService = inject(OutkeepPermissionService);
  public $selectedRequests = this.requestService.selectedRequests;
  public $selectedRequestsAddresses = computed(() =>
    this.$selectedRequests()
      .map(request => request.actualDeliveryAddress)
      .filter(Boolean),
  );
  public formGroup = this.formBuilder.group({
    [FormFields.ActualDeliveryAddress]: ['', Validators.required],
  });

  $viewObserver = input.required<Subject<never>>({
    alias: 'viewObserver',
  });

  public isEnteredActualDeliveryAddressVerified = false;

  get $isDeliveryAddressDifferentFromActual() {
    return computed(() =>
      this.$selectedRequests().some(
        this.requestAddressesService.isDeliveryAddressDifferentFromActual,
      ),
    );
  }

  public setActualDeliveryAddress() {
    if (this.actualDeliveryAddressToSet.value) {
      this.requestAddressesService
        .setActualDeliveryAddress$(
          this.actualDeliveryAddressToSet.value,
          this.$selectedRequests(),
        )
        .pipe(
          switchMap(() => {
            return this.requestService.reloadSelectedRequests$();
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => this.$viewObserver().complete());
    }
  }

  @pure
  get actualDeliveryAddressToSet() {
    return this.formGroup.controls[FormFields.ActualDeliveryAddress];
  }

  public onCheckAddressButtonClick() {
    this.isEnteredActualDeliveryAddressVerified = true;
    asyncScheduler.schedule(() => {
      const element = document.getElementById('verifyButton');

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }, 300);
  }

  protected readonly stringifyFn = (address: string) => address;

  public ngOnInit(): void {
    this.actualDeliveryAddressToSet.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => (this.isEnteredActualDeliveryAddressVerified = false));
  }
}
